import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import { vatRateService } from '../services/vatRateService';

const VatRates = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [vatRates, setVatRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRate, setNewRate] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchVatRates = useCallback(async () => {
    try {
      const res = await vatRateService.getVatRates();
      setVatRates(res.data || []);
    } catch {
      setVatRates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVatRates();
  }, [fetchVatRates]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');

    const parsed = parseFloat(newRate);
    if (newRate === '' || isNaN(parsed)) {
      setAddError('Please enter a valid number.');
      return;
    }

    setAddLoading(true);
    try {
      await vatRateService.createVatRate({ rate: parsed });
      setNewRate('');
      showSuccess('VAT rate added successfully.');
      fetchVatRates();
    } catch (error) {
      const msg =
        error.response?.data?.errors?.rate?.[0] ||
        error.response?.data?.message ||
        'An error occurred. Please try again.';
      setAddError(msg);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (vatRate) => {
    try {
      await vatRateService.deleteVatRate(vatRate.id);
      setDeleteConfirm(null);
      showSuccess('VAT rate deleted successfully.');
      fetchVatRates();
    } catch {
      setDeleteConfirm(null);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const formatRate = (rate) => `${parseFloat(rate)}%`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">VAT Rates</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">
                Welcome, {user?.name}!
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-2.5 py-8">
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm text-green-700 dark:text-green-300">
            {successMessage}
          </div>
        )}

        {/* Add Rate Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Custom VAT Rate</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Enter a percentage value between 0 and 100.</p>
          </div>
          <div className="p-6">
            <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3 items-start">
              <div className="flex-1 w-full sm:w-auto">
                <div className="relative">
                  <input
                    type="number"
                    value={newRate}
                    onChange={(e) => { setNewRate(e.target.value); setAddError(''); }}
                    placeholder="e.g. 21.5"
                    step="0.01"
                    min="0"
                    max="100"
                    className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-8 ${
                      addError
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
                    }`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none">%</span>
                </div>
                {addError && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{addError}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={addLoading}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {addLoading ? 'Adding...' : 'Add Rate'}
              </button>
            </form>
          </div>
        </div>

        {/* Rates List Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All VAT Rates</h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="inline-flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading...
              </div>
            </div>
          ) : vatRates.length === 0 ? (
            <div className="py-16 text-center text-gray-400 dark:text-gray-500">
              No VAT rates found.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rate</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="sticky right-0 text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" style={{ backgroundColor: '#2b3544' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {vatRates.map((vr) => (
                      <tr key={vr.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white text-lg">{formatRate(vr.rate)}</td>
                        <td className="px-6 py-4">
                          {vr.is_default ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">Default</span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">Custom</span>
                          )}
                        </td>
                        <td className="sticky right-0 bg-white dark:bg-gray-800 px-6 py-4 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]">
                          {!vr.is_default && (
                            <button
                              onClick={() => setDeleteConfirm(vr)}
                              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {vatRates.map((vr) => (
                  <div key={vr.id} className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold text-gray-900 dark:text-white">{formatRate(vr.rate)}</span>
                      {vr.is_default ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">Default</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">Custom</span>
                      )}
                    </div>
                    {!vr.is_default && (
                      <button
                        onClick={() => setDeleteConfirm(vr)}
                        className="text-sm text-red-600 dark:text-red-400 font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete VAT Rate</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete the <strong>{formatRate(deleteConfirm.rate)}</strong> VAT rate? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VatRates;
